import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Input, FormField, ToggleGroup } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t('login.invalid_credentials');
      setError(typeof msg === 'string' ? msg : t('login.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col justify-between p-10 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-100px] left-[-100px] w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-[-80px] right-[-80px] w-80 h-80 rounded-full bg-accent/20 blur-3xl" />
          <div className="absolute top-1/2 right-[-60px] w-56 h-56 rounded-full bg-white/5 blur-2xl" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white grid place-items-center flex-shrink-0">
            <span className="text-primary font-black text-[14px] tracking-tight">BOD</span>
          </div>
          <div>
            <p className="text-white font-bold text-[15px] leading-tight">{t('brand.name')}</p>
            <p className="text-white/50 text-[11px]">{t('brand.tagline')}</p>
          </div>
        </div>

        {/* Headline */}
        <div className="relative">
          <h1 className="text-4xl font-bold leading-tight text-white mb-4">
            {i18n.language === 'fr'
              ? <>Suivi en temps réel de vos{' '}<span className="text-accent">factures fournisseurs.</span></>
              : <>Real-time tracking of your{' '}<span className="text-accent">supplier invoices.</span></>
            }
          </h1>
          <p className="text-white/60 text-[13px] leading-relaxed max-w-sm">
            {t('login.subheadline')}
          </p>

          {/* Stats */}
          <div className="mt-10 flex gap-8">
            <div>
              <p className="text-white font-bold text-xl">100%</p>
              <p className="text-white/50 text-[11px] mt-0.5">{t('login.stat1')}</p>
            </div>
            <div>
              <p className="text-white font-bold text-xl">0</p>
              <p className="text-white/50 text-[11px] mt-0.5">{t('login.stat2')}</p>
            </div>
            <div>
              <p className="text-white font-bold text-xl">ERP</p>
              <p className="text-white/50 text-[11px] mt-0.5">{t('login.stat3')}</p>
            </div>
          </div>
        </div>

        <p className="relative text-white/30 text-[11px]">{t('brand.footer')}</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col bg-white">
        <div className="flex justify-end p-6">
          <ToggleGroup
            options={[{ value: 'fr', label: 'FR' }, { value: 'en', label: 'EN' }]}
            value={i18n.language === 'en' ? 'en' : 'fr'}
            onChange={(lang) => i18n.changeLanguage(lang)}
          />
        </div>

        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">{t('login.welcome')}</h2>
              <p className="text-[13px] text-slate-500 mt-1">{t('login.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label={t('login.email_label')} required>
                <Input
                  type="email"
                  placeholder={t('login.email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </FormField>

              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5 block">
                  {t('login.password_label')} <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                    tabIndex={-1}
                  >
                    {showPassword
                      ? <EyeOff size={14} strokeWidth={1.75} />
                      : <Eye size={14} strokeWidth={1.75} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                iconRight={!loading ? ArrowRight : undefined}
                className="w-full mt-2"
              >
                {t('login.submit')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
